import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import type {
  DealStage,
  DealStageType,
  Pipeline,
} from '@shared/domain/accountPlan/dealStage.types';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApi } from '@shared/services/api';

interface StageFormState {
  label: string;
  color: string;
  order: number;
  stageCriteria: string;
  type: DealStageType;
}

const PRESET_COLORS = [
  '#4e7c5f', '#3498db', '#6b5b8a', '#c05a3a', '#D4AF37',
  '#2196f3', '#e91e63', '#009688', '#ff9800', '#607d8b',
];

const STAGE_TYPES: { value: DealStageType; label: string }[] = [
  { label: 'Open', value: 'open' },
  { label: 'Closed / Won', value: 'closed_won' },
  { label: 'Closed / Lost', value: 'closed_lost' },
];

function AdminPipelinesTable(): React.ReactElement {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<StageFormState>({
    color: '#3498db',
    label: '',
    order: 0,
    stageCriteria: '',
    type: 'open',
  });
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const selectedPipeline = pipelines.find(p => p.id === selectedPipelineId) ?? null;
  const rows = selectedPipeline?.stages ?? [];

  const fetchPipelines = useCallback(async (showLoading = false): Promise<void> => {
    if (showLoading) setInitialLoading(true);
    const result = await apiRef.current.get('pipeline');
    if (result?.error) {
      setError(result.message ?? 'Failed to load pipelines');
    }
    else {
      const list = (result?.data ?? []) as Pipeline[];
      setPipelines(list);
      setError(null);
      if (selectedPipelineId === null && list.length > 0) {
        const def = list.find(p => p.isDefault) ?? list[0];
        setSelectedPipelineId(def.id);
      }
    }
    setInitialLoading(false);
  }, [selectedPipelineId]);

  useEffect(() => {
    fetchPipelines(true);
  }, [fetchPipelines]);

  const handleOpenCreate = useCallback((): void => {
    setEditingId(null);
    setForm({
      color: '#3498db',
      label: '',
      order: rows.length,
      stageCriteria: '',
      type: 'open',
    });
    setDialogOpen(true);
  }, [rows.length]);

  const handleOpenEdit = useCallback((row: DealStage): void => {
    setEditingId(row.id);
    setForm({
      color: row.color ?? '#3498db',
      label: row.label,
      order: row.order,
      stageCriteria: row.stageCriteria ?? '',
      type: row.type,
    });
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(async (): Promise<void> => {
    if (!form.label.trim() || !selectedPipelineId) return;
    setSaving(true);
    try {
      const payload = {
        color: form.color,
        label: form.label,
        order: form.order,
        stageCriteria: form.stageCriteria || null,
        type: form.type,
      };
      if (editingId) {
        await apiRef.current.put(`pipeline/stages/${editingId}`, payload);
      }
      else {
        await apiRef.current.post(`pipeline/${selectedPipelineId}/stages`, payload);
      }
      setDialogOpen(false);
      await fetchPipelines();
    }
    finally {
      setSaving(false);
    }
  }, [editingId, form, fetchPipelines, selectedPipelineId]);

  const handleDelete = useCallback(async (id: number): Promise<void> => {
    await apiRef.current.delete(`pipeline/stages/${id}`);
    setDeleteConfirm(null);
    await fetchPipelines();
  }, [fetchPipelines]);

  const columns: GridColDef[] = useMemo(() => [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'order', headerName: 'Pos', width: 70 },
    {
      field: 'color',
      headerName: 'Color',
      width: 80,
      renderCell: (params: GridRenderCellParams<DealStage>) => (
        <Box
          sx={{
            backgroundColor: (params.value as string) ?? '#607d8b',
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: '50%',
            height: 20,
            width: 20,
          }}
        />
      ),
    },
    { field: 'label', headerName: 'Stage Name', flex: 1, minWidth: 150 },
    {
      field: 'type',
      headerName: 'Type',
      width: 140,
      renderCell: (params: GridRenderCellParams<DealStage>) => {
        const entry = STAGE_TYPES.find(t => t.value === params.value);
        return <span>{entry?.label ?? String(params.value)}</span>;
      },
    },
    {
      field: 'stageCriteria',
      headerName: 'Stage Criteria',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams<DealStage>) => (
        <Typography variant="body2" noWrap title={(params.value as string) ?? ''}>
          {(params.value as string) ?? '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams<DealStage>) => (
        <Box>
          <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setDeleteConfirm(params.row.id)}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ], [handleOpenEdit]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
          <Typography variant="h6">Pipelines</Typography>
          <Select
            value={selectedPipelineId ?? ''}
            onChange={e => setSelectedPipelineId(Number(e.target.value))}
            size="small"
            sx={{ minWidth: 240 }}
          >
            {pipelines.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
                {p.scope === 'public' ? ' (public)' : ''}
                {p.isDefault ? ' — default' : ''}
              </MenuItem>
            ))}
          </Select>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          size="small"
          disabled={!selectedPipelineId}
          sx={{ textTransform: 'none' }}
        >
          Add Stage
        </Button>
      </Box>

      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Stages defined here apply to all account plans using this pipeline. Each
        stage's criteria scopes which signals are prioritized during extraction.
      </Typography>

      <DataGridPro
        rows={rows}
        columns={columns}
        autoHeight
        disableRowSelectionOnClick
        initialState={{
          sorting: { sortModel: [{ field: 'order', sort: 'asc' }] },
        }}
        sx={{
          '& .MuiDataGrid-cell': { fontSize: '0.85rem' },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5' },
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Deal Stage' : 'Create Deal Stage'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Stage Name"
            value={form.label}
            onChange={e => setForm(prev => ({ ...prev, label: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mb: 2, mt: 1 }}
          />
          <Typography variant="body2" sx={{ mb: 1 }}>Type</Typography>
          <Select
            value={form.type}
            onChange={e => setForm(prev => ({ ...prev, type: e.target.value as DealStageType }))}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          >
            {STAGE_TYPES.map(t => (
              <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
            ))}
          </Select>
          <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {PRESET_COLORS.map(c => (
              <Box
                key={c}
                onClick={() => setForm(prev => ({ ...prev, color: c }))}
                sx={{
                  backgroundColor: c,
                  border: c === form.color ? '3px solid #333' : '2px solid transparent',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  height: 28,
                  width: 28,
                }}
              />
            ))}
          </Box>
          <TextField
            label="Position"
            type="number"
            value={form.order}
            onChange={e => setForm(prev => ({ ...prev, order: Number(e.target.value) }))}
            fullWidth
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Stage Criteria"
            value={form.stageCriteria}
            onChange={e => setForm(prev => ({ ...prev, stageCriteria: e.target.value }))}
            fullWidth
            size="small"
            multiline
            rows={3}
            helperText="What's true when a deal reaches this stage?"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.label.trim()}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)} maxWidth="xs">
        <DialogTitle>Delete Deal Stage</DialogTitle>
        <DialogContent>
          <Typography>Are you sure? Plans in this stage will become unassigned.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
            sx={{ textTransform: 'none' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminPipelinesTable;
